import { useEffect, useSyncExternalStore } from "react";
import type { RealtimeActiveScope } from "./types";

type Listener = () => void;

const listeners = new Set<Listener>();
const activePresenceRooms = new Map<string, number>();
const activeLiveRoomStates = new Map<string, number>();
let snapshot: RealtimeActiveScope[] = [];

function emit() {
  snapshot = [
    ...[...activeLiveRoomStates.keys()].map((accessId) => ({
      scope: "liveRoom.state" as const,
      accessId,
    })),
    ...[...activePresenceRooms.keys()].map((accessId) => ({
      scope: "presence.room" as const,
      accessId,
    })),
  ];
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeRealtimeActiveScopes(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getRealtimeActiveScopesSnapshot() {
  return snapshot;
}

export function registerRealtimePresenceRoom(accessId: string) {
  return registerRealtimeRoomScope(activePresenceRooms, accessId);
}

export function registerRealtimeLiveRoomState(accessId: string) {
  return registerRealtimeRoomScope(activeLiveRoomStates, accessId);
}

function registerRealtimeRoomScope(
  scopeCounts: Map<string, number>,
  accessId: string,
) {
  const currentCount = scopeCounts.get(accessId) ?? 0;
  scopeCounts.set(accessId, currentCount + 1);
  if (currentCount === 0) {
    emit();
  }

  return () => {
    const nextCount = (scopeCounts.get(accessId) ?? 1) - 1;
    if (nextCount <= 0) {
      scopeCounts.delete(accessId);
      emit();
      return;
    }
    scopeCounts.set(accessId, nextCount);
  };
}

export function useRealtimeActiveScopes() {
  return useSyncExternalStore(
    subscribeRealtimeActiveScopes,
    getRealtimeActiveScopesSnapshot,
    getRealtimeActiveScopesSnapshot,
  );
}

export function useRealtimePresenceRoomScope(
  accessId: string,
  enabled: boolean = true,
) {
  useEffect(() => {
    if (!accessId || !enabled) return;
    return registerRealtimePresenceRoom(accessId);
  }, [accessId, enabled]);
}

export function useRealtimeLiveRoomStateScope(
  accessId: string,
  enabled: boolean = true,
) {
  useEffect(() => {
    if (!accessId || !enabled) return;
    return registerRealtimeLiveRoomState(accessId);
  }, [accessId, enabled]);
}
